export const API_BASE_URL = 
  import.meta.env.VITE_API_BASE_URL || 'https://pwgfmn7vlwj6sz7vcwxh7nrjpy0hfekw.lambda-url.ca-central-1.on.aws/api/v1';

export const getApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  return `${baseUrl}/${cleanEndpoint}`;
};

/**
 * WHICH organization this site serves (T090426001 / T090426002).
 *
 * A site is one tenant's shopfront, so ONE variable names it and both the forum
 * and the job board read it. `VITE_FORUM_ORG_SLUG` is still honoured as a
 * fallback because it shipped first and existing deployments set it; a build
 * that sets only the old name keeps working, and one that sets `VITE_ORG_SLUG`
 * gets jobs scoped too.
 *
 * Left EMPTY (the default), no slug is sent and the API falls back to its own
 * configured default org for both surfaces — exactly today's behaviour, so a
 * deployment that never sets this is unaffected.
 *
 * A slug, not an organization id: it is a public, immutable handle the API
 * resolves server-side. Nothing here can name a tenant by raw id.
 */
export const ORG_SLUG: string =
  import.meta.env.VITE_ORG_SLUG || import.meta.env.VITE_FORUM_ORG_SLUG || '';

/** @deprecated Use `ORG_SLUG`. Kept so existing imports keep compiling. */
export const FORUM_ORG_SLUG: string = ORG_SLUG;

/**
 * The forum READ path for this site's organization.
 *
 * Reads go to the PUBLIC, org-addressed surface — `/public/org/{slug}/forum/...`
 * — not to the first-party `/forum/...` route with an `?org=` query.
 *
 * That distinction is what makes a client's own website work at all. CORS on
 * `/public/*` is governed by the organization's OWN registered-website list, the
 * one an owner fills in under Public site settings. `/forum/*` sits behind the
 * app's fixed allowlist instead, which knows nothing about tenant origins — so a
 * browser on the client's domain gets a 403 there no matter what the owner
 * registers. Confirmed against staging: `?org=` + Origin -> 403, the public
 * org-addressed path + the same Origin -> 200.
 *
 * `?org=` was how this worked before the org-addressed route existed; jobs moved
 * across when it shipped and the forum did not. This closes that gap.
 *
 * With no slug configured, the bare `forum/...` path is emitted, which is
 * unchanged behaviour for a site serving the API's default organization.
 *
 * `suffix` is everything after `forum` — `/subforums`, `/posts/{id}/comments`.
 */
export const forumPath = (suffix = '', query = ''): string => {
  const base = ORG_SLUG
    ? `public/org/${encodeURIComponent(ORG_SLUG)}/forum`
    : 'forum';
  return `${base}${suffix}${query ? `?${query}` : ''}`;
};

/**
 * WRITES stay on the first-party `/forum/...` route.
 *
 * The public org-addressed surface registers no POST at all, by design: a write
 * has to be attributable, so it belongs on the keyed partner surface behind an
 * `ak_` key that cannot live in a browser. Straight from the browser, therefore,
 * posting reaches only the API's default organization — and from a client domain
 * it does not reach it at all, for the CORS reason above. `forumWriteUrl` below
 * is the way past both limits, once the server-side proxy is deployed.
 *
 * Kept as a separate, named helper rather than folded into `forumPath` so that
 * limitation is visible at the call site instead of implied.
 */
export const withForumOrg = (endpoint: string): string => {
  if (!ORG_SLUG) return endpoint;
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}org=${encodeURIComponent(ORG_SLUG)}`;
};

/**
 * WHERE forum WRITES go (T090426001, forum spec F7).
 *
 * Empty (the default) means writes go straight to `/forum/*` on the API, exactly
 * as they do today: anonymous, landing in the API's own configured default
 * forum. That keeps this deployable before the proxy exists.
 *
 * Set to the proxy's mount path — e.g. `/api/forum` — writes go there instead,
 * and the proxy attaches an `ak_` key with `forum:write` server-side. The key
 * never reaches the browser, which is the whole requirement F7 states.
 *
 * A PATH, not an origin, so the proxy is same-origin with the site and needs no
 * CORS preflight in the ordinary case. An absolute URL works too if the infra
 * decision lands somewhere else.
 */
export const FORUM_WRITE_PROXY: string = import.meta.env.VITE_FORUM_WRITE_PROXY || '';

/**
 * The URL for one forum write.
 *
 * `endpoint` is the API-relative path (`forum/posts`), and the proxy equivalent
 * drops the `forum/` prefix because the proxy is already mounted at the forum.
 *
 * NO `org` SLUG on the proxy path, deliberately. Tenancy comes from the key row
 * on the partner surface; a slug in the query string is ignored there, and the
 * API now ignores it on the direct path too — `?org=` became read-only when the
 * cross-tenant write hole was closed. Sending one would imply a control the
 * caller does not have.
 */
export const forumWriteUrl = (endpoint: string): string => {
  if (!FORUM_WRITE_PROXY) return getApiUrl(withForumOrg(endpoint));
  const base = FORUM_WRITE_PROXY.replace(/\/+$/, '');
  const rest = endpoint.replace(/^\/+/, '').replace(/^forum\//, '');
  return `${base}/${rest}`;
};

/**
 * The job-board path for this site's organization (T090426002).
 *
 * Jobs are addressed by a PATH SEGMENT, not a query string:
 * `/public/org/{slug}/jobs`. That is the scheme the API settled on — the slug is
 * part of the resource's address, so it survives sharing and indexing, which a
 * `?org=` on a careers page would not.
 *
 * Deliberately different from the forum's `withForumOrg` for that reason, rather
 * than by accident. Both resolve the slug server-side and neither accepts an
 * organization id.
 *
 * With no slug configured this returns the bare `public/jobs...` path, which
 * serves whichever organization the API's own board-slug config names.
 *
 * `suffix` is everything after `jobs` — '' for the list, `/{id}` for one
 * posting, `/{id}/apply` to apply. All three exist on both surfaces.
 */
export const jobsPath = (suffix = '', query = ''): string => {
  const base = ORG_SLUG
    ? `public/org/${encodeURIComponent(ORG_SLUG)}/jobs`
    : 'public/jobs';
  return `${base}${suffix}${query ? `?${query}` : ''}`;
};
